import type { FC } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import type { PaidMediaOverviewFunnelPlatform } from "src/services/PaidMediaOverviewAPI";
import { formatOverviewNumber, formatOverviewPct } from "../../utils/paidMediaOverviewFormat";
import { overviewPlatformDescriptor } from "../../data/paidMediaOverviewPlatforms";
import {
  OverviewChartEmptyState,
  OverviewChartLoadingState,
  OverviewMetricTip,
} from "./overviewPrimitives";

const FUNNEL_STAGE_WIDTHS = ["full", "mid", "low"] as const;

interface FunnelComparisonCardProps {
  funnelPlatforms: PaidMediaOverviewFunnelPlatform[];
  loading: boolean;
}

const FunnelComparisonCard: FC<FunnelComparisonCardProps> = ({ funnelPlatforms, loading }) => {
  const { t } = useTranslation();

  const renderFunnels = () => {
    if (loading) {
      return <OverviewChartLoadingState />;
    }
    if (!funnelPlatforms?.length) {
      return <OverviewChartEmptyState />;
    }

    return (
      <div className="paid-media-overview__funnel-grid">
        {funnelPlatforms.map(platform => (
          <Funnel key={platform?.platform} platform={platform} t={t} />
        ))}
      </div>
    );
  };

  return (
    <article className="paid-media-panel">
      <div className="paid-media-overview__panel-header">
        <div>
          <h3>
            {t("paidMediaOverview.conversion.funnelTitle")}
            <OverviewMetricTip
              id="clickToPurchase"
              label={t("paidMediaOverview.conversion.funnelTitle")}
            />
          </h3>
          <p className="paid-media-overview__panel-subtitle">
            {t("paidMediaOverview.conversion.funnelSubtitle")}
          </p>
        </div>
      </div>
      {renderFunnels()}
    </article>
  );
};

const Funnel: FC<{
  platform: PaidMediaOverviewFunnelPlatform;
  t: TFunction;
}> = ({ platform, t }) => {
  const descriptor = overviewPlatformDescriptor(platform?.platform);

  const stages = [
    {
      key: "impressions",
      tooltipId: "impressions",
      label: t("paidMediaOverview.metrics.impressions"),
      value: platform?.impressions ?? null,
    },
    {
      key: "clicks",
      tooltipId: "clicks",
      label: t(descriptor?.clicksLabelKey ?? "paidMediaOverview.metrics.clicks"),
      value: platform?.clicks ?? null,
    },
    {
      key: "purchases",
      tooltipId: "paidPurchases",
      label: t(descriptor?.purchasesLabelKey ?? "paidMediaOverview.metrics.purchases"),
      value: platform?.purchases ?? null,
    },
  ];

  return (
    <div className="paid-media-overview__funnel" data-platform={descriptor?.slug ?? "unknown"}>
      <div className="paid-media-overview__funnel-title">
        <span className="paid-media-overview__funnel-dot" aria-hidden />
        {descriptor ? t(descriptor.labelKey) : ""}
      </div>
      <div className="paid-media-overview__funnel-stages">
        {stages.map((stage, index) => (
          <div key={stage.key}>
            <div className="paid-media-overview__funnel-stage-row">
              <span>
                {stage.label}
                <OverviewMetricTip id={stage.tooltipId} label={stage.label} />
              </span>
              <span className="paid-media-overview__funnel-stage-value">
                {formatOverviewNumber(stage.value)}
              </span>
            </div>
            <div className="paid-media-overview__funnel-bar-progress-bar-center">
              <div
                className="paid-media-overview__funnel-bar"
                data-width={FUNNEL_STAGE_WIDTHS[index]}
              />
            </div>
          </div>
        ))}
      </div>
      <div className="paid-media-overview__funnel-footer">
        <div>
          <div className="paid-media-overview__funnel-footer-label">
            {t("paidMediaOverview.metrics.ctr")}
            <OverviewMetricTip id="ctr" label={t("paidMediaOverview.metrics.ctr")} />
          </div>
          <div className="paid-media-overview__funnel-footer-value">
            {formatOverviewPct(platform?.ctr, 2)}
          </div>
        </div>
        <div>
          <div className="paid-media-overview__funnel-footer-label">
            {t("paidMediaOverview.conversion.clickToPurchase")}
            <OverviewMetricTip
              id="clickToPurchase"
              label={t("paidMediaOverview.conversion.clickToPurchaseLabel")}
            />
          </div>
          <div className="paid-media-overview__funnel-footer-value">
            {formatOverviewPct(platform?.click_to_purchase_rate, 2)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FunnelComparisonCard;
